import { createClient } from '../../client/createClient'
import { createSubscan } from '../../explorer'
import type { Chain } from '../types'

type CreateHydrationChainParameters = {
  explorerApiKey?: string
  explorerProxyUrl?: string
  rpcEndpoints?: string[]
}

export const createHydrationChain = ({
  explorerApiKey,
  explorerProxyUrl,
  rpcEndpoints = hydrationWebsocketRpcUrls,
}: CreateHydrationChainParameters): Chain => {
  return {
    /** {@link https://polkadot.subscan.io/parachain_list?status=parachain} */
    id: 2034,
    /** {@link https://github.com/paritytech/ss58-registry/blob/main/ss58-registry.json} */
    ss58Prefix: 0,
    network: 'hydration',
    name: 'Hydration',
    blockTimeSeconds: 8,
    nativeToken: {
      name: 'Hydration',
      symbol: 'HDX',
      decimals: 12,
      currency: {
        symbol: 'HDX',
        type: 'CryptoCurrency',
        id: 'hydradx',
        name: 'Hydration',
        updatedAt: 0,
      },
    },
    client: createClient(rpcEndpoints),
    explorer: createSubscan({
      name: 'Subscan',
      baseUrl: 'https://hydration.subscan.io',
      networkName: 'hydration',
      apiKey: explorerApiKey,
      proxyUrl: explorerProxyUrl,
    }),
    rpcEndpoints,
    assetType: 'tokens',
    /** {@link https://assethub-polkadot.subscan.io/asset_token?page=1&order=desc&order_field=holders} */
    assetMap: new Map([
      [
        5n,
        {
          asset: {
            symbol: 'DOT',
            type: 'CryptoCurrency',
            id: 'polkadot',
            name: 'Polkadot',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        8n,
        {
          asset: {
            symbol: 'PHA',
            type: 'CryptoCurrency',
            id: 'pha',
            name: 'PHALA',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        9n,
        {
          asset: {
            symbol: 'ASTR',
            type: 'CryptoCurrency',
            id: 'astar',
            name: 'Astar',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        10n,
        {
          asset: {
            symbol: 'USDT',
            type: 'CryptoCurrency',
            id: 'tether',
            name: 'Tether',
            updatedAt: 0,
          },
          decimals: 6,
        },
      ],
      [
        14n,
        {
          asset: {
            symbol: 'BNC',
            type: 'CryptoCurrency',
            id: 'bifrost-native-coin',
            name: 'Bifrost',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        15n,
        {
          asset: {
            symbol: 'vDOT',
            type: 'CryptoCurrency',
            id: 'voucher-dot',
            name: 'Voucher DOT',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        16n,
        {
          asset: {
            symbol: 'GLMR',
            type: 'CryptoCurrency',
            id: 'moonbeam',
            name: 'Moonbeam',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        17n,
        {
          asset: {
            symbol: 'INTR',
            type: 'CryptoCurrency',
            id: 'interlay',
            name: 'Interlay',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        19n,
        {
          asset: {
            symbol: 'WBTC',
            type: 'CryptoCurrency',
            id: 'wrapped-bitcoin',
            name: 'Wrapped Bitcoin',
            updatedAt: 0,
          },
          decimals: 8,
        },
      ],
      [
        20n,
        {
          asset: {
            symbol: 'ETH',
            type: 'CryptoCurrency',
            id: 'ethereum',
            name: 'Ethereum',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        21n,
        {
          asset: {
            symbol: 'USDC',
            type: 'CryptoCurrency',
            id: 'usd-coin',
            name: 'USDC',
            updatedAt: 0,
          },
          decimals: 6,
        },
      ],
      [
        22n,
        {
          asset: {
            symbol: 'USDC',
            type: 'CryptoCurrency',
            id: 'usd-coin',
            name: 'USDC',
            updatedAt: 0,
          },
          decimals: 6,
        },
      ],
      [
        23n,
        {
          asset: {
            symbol: 'USDT',
            type: 'CryptoCurrency',
            id: 'tether',
            name: 'Tether',
            updatedAt: 0,
          },
          decimals: 6,
        },
      ],
      [
        25n,
        {
          asset: {
            symbol: 'UNQ',
            type: 'CryptoCurrency',
            id: 'unique-network',
            name: 'Unique Network',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        33n,
        {
          asset: {
            symbol: 'vASTR',
            type: 'CryptoCurrency',
            id: 'bifrost-voucher-astr',
            name: 'Bifrost Voucher ASTR',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        34n,
        {
          asset: {
            symbol: 'ETH',
            type: 'CryptoCurrency',
            id: 'ethereum',
            name: 'Ethereum',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        35n,
        {
          asset: {
            symbol: 'TRAC',
            type: 'CryptoCurrency',
            id: 'origintrail',
            name: 'OriginTrail',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        36n,
        {
          asset: {
            symbol: 'NEURO',
            type: 'CryptoCurrency',
            id: 'neurowebai',
            name: 'NeuroWeb',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        39n,
        {
          asset: {
            symbol: 'PAXG',
            type: 'CryptoCurrency',
            id: 'pax-gold',
            name: 'PAX Gold',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        1000081n,
        {
          asset: {
            symbol: 'PEN',
            type: 'CryptoCurrency',
            id: 'pendulum-chain',
            name: 'Pendulum',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        1000624n,
        {
          asset: {
            symbol: 'AAVE',
            type: 'CryptoCurrency',
            id: 'aave',
            name: 'AAVE',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        1000752n,
        {
          asset: {
            symbol: 'SOL',
            type: 'CryptoCurrency',
            id: 'solana',
            name: 'Solana',
            updatedAt: 0,
          },
          decimals: 9,
        },
      ],
      [
        1000753n,
        {
          asset: {
            symbol: 'SUI',
            type: 'CryptoCurrency',
            id: 'sui',
            name: 'Sui',
            updatedAt: 0,
          },
          decimals: 9,
        },
      ],
      [
        1000765n,
        {
          asset: {
            symbol: 'tBTC',
            type: 'CryptoCurrency',
            id: 'tbtc',
            name: 'tBTC',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        1000771n,
        {
          asset: {
            symbol: 'KSM',
            type: 'CryptoCurrency',
            id: 'kusama',
            name: 'Kusama',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        1000794n,
        {
          asset: {
            symbol: 'LINK',
            type: 'CryptoCurrency',
            id: 'chainlink',
            name: 'Chainlink',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        1000795n,
        {
          asset: {
            symbol: 'SKY',
            type: 'CryptoCurrency',
            id: 'sky',
            name: 'Sky',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        1000796n,
        {
          asset: {
            symbol: 'LDO',
            type: 'CryptoCurrency',
            id: 'lido-dao',
            name: 'Lido DAO',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        1000809n,
        {
          asset: {
            symbol: 'wstETH',
            type: 'CryptoCurrency',
            id: 'wrapped-steth',
            name: 'Wrapped stETH',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
    ]),
  }
}

export const hydrationWebsocketRpcUrls = [
  'wss://hydration-rpc.n.dwellir.com',
  'wss://rpc.hydradx.cloud',
  'wss://rpc.helikon.io/hydradx',
  'wss://hydration.ibp.network',
  'wss://hydration.dotters.network',
]
